import React, { useEffect } from 'react'
import { Trans, withTranslation, WithTranslation } from 'react-i18next'
import Flex from 'typescript-styled-flex'
import Scrollbars from 'react-custom-scrollbars'
import { FiPlus } from 'react-icons/fi'
import { isEmpty } from 'lodash'
import {
  ModalFlex,
  ModalHeader,
  ModalHeaderLabel,
} from '../../../components/Template/Modal'
import {
  ProvenanceItem,
  ServicesModalWrapper,
} from '../../../components/Calendar/ReservationModal'
import Button from '../../../components/Template/Button'
import { TextInput } from '../../../components/FormBuilder'
import AddButton from '../../../components/AddButton'
import { IProvenance } from '../../../models/Provenance'
import sanitizeInput from '../../../utils/InputFiltering'
import styled from '../../../theme'
import Icon from '../../../components/Template/Modal/CloseButton/Icon'
import useSize from '../../../hooks/useSize'
import { IWithSize } from '../Calendar/Calendar'

function isValidProvenance(provenances: IProvenance[], provenanceName: string) {
  return (
    !isEmpty(provenanceName.trim()) &&
    !provenances.some(p => p.name === provenanceName)
  )
}

const Scroll = styled(Scrollbars)<IWithSize>`
  & > div {
    // "Fix" to black rectangle showing on mobile chrome after keyboard is hidden
    margin-top: ${({ size }) => (size ? -10 * size : -10)}px;
    padding-top: ${({ size }) => (size ? 10 * size : 10)}px;
    overflow-x: hidden !important;
    scroll-behavior: smooth;
    overscroll-behavior: contain;
  }
`

function ProvenancesModal({
  close,
  open,
  t,
  addProvenance,
  provenances,
  provenancesWithInitial,
  deleteProvenance,
}: { children?: React.ReactNode } & IProvenancesModalProps &
  IProvenancesModalDispatch &
  WithTranslation) {
  const [provenance, changeProvenance] = React.useState('')
  const [isValid, setIsValid] = React.useState(false)
  const size = useSize()

  const nonBookingProvenances = provenances
    ? provenances.filter((provenance) => provenance.name !== 'booking.com')
    : []

  useEffect(() => {
    setIsValid(
      isValidProvenance(provenancesWithInitial, sanitizeInput(provenance)),
    )
  }, [provenancesWithInitial, provenance])

  function onChange(e: React.SyntheticEvent<HTMLInputElement>) {
    changeProvenance(
      e.currentTarget.value.replace(
        /([\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
        '',
      ),
    )
  }

  function onAdd() {
    if (isValid) {
      addProvenance(sanitizeInput(provenance))
      changeProvenance('')
    }
  }

  function save() {
    onAdd()
    close()
  }

  return (
    <ModalFlex open={open} onClose={close}>
      <ServicesModalWrapper style={{ width: '344px' }}>
        <ModalHeader style={{ padding: `${16 * size}px`, paddingBottom: '0' }}>
          <Flex style={{ width: '100%' }} justifyStart={true}>
            <ModalHeaderLabel
              style={{
                fontSize: `${16 * size}px`,
                padding: `${5 * size}px 0px`,
              }}
            >
              <Trans i18nKey={'reservation_source'}>Pochodzenie</Trans>
            </ModalHeaderLabel>
          </Flex>
          <Flex style={{ width: '20% ' }} justifyEnd={true} alignCenter={true}>
            <Icon
              style={{
                fontSize: `${20 * size}px`,
                marginRight: `${-4 * size}px`,
              }}
              onClick={close}
            />
          </Flex>
        </ModalHeader>
        <Flex
          style={{
            flexBasis: 'unset',
            margin: `${16 * size}px 0px 0px ${16 * size}px`,
          }}
        >
          <Scroll
            size={size}
            autoHeight={true}
            autoHeightMax={'50vh'}
            hideTracksWhenNotNeeded={true}
          >
            {nonBookingProvenances.map((provenance) => (
              <ProvenanceItem
                size={size}
                key={`provenance-item-${provenance.id}`}
                onDelete={deleteProvenance(provenance.id)}
                label={provenance.name}
              />
            ))}
          </Scroll>
        </Flex>
        <Flex
          row={true}
          full={true}
          style={{
            padding: `${10 * size}px ${16 * size}px 0px ${16 * size}px`,
          }}
        >
          <TextInput.Field
            size={size}
            value={provenance}
            style={{
              height: `${38 * size}px`,
              maxHeight: `${38 * size}px`,
              minHeight: `${38 * size}px`,
            }}
            width={'100%'}
            wrapperStyle={{
              height: `${38 * size}px`,
              maxHeight: `${38 * size}px`,
              minHeight: `${38 * size}px`,
            }}
            inputStyle={{
              borderRadius: `${4 * size}px 0px 0px ${4 * size}px`,
              height: `${38 * size}px`,
              lineHeight: `${38 * size}px`,
              maxHeight: `${38 * size}px`,
              minHeight: `${38 * size}px`,
            }}
            onChange={onChange}
          />
          <AddButton
            size={size}
            onClick={isValid ? onAdd : null}
            disabled={!isValid}
            style={{
              borderRadius: '0px 3px 3px 0px',
              height: `${38 * size}px`,
              minWidth: `${38 * size}px`,
              width: `${38 * size}px`,
            }}
          >
            <FiPlus size={`${16 * size}px`} />
          </AddButton>
        </Flex>
        <Flex
          row={true}
          style={{
            margin: `${16 * size}px`,
          }}
        >
          <Button
            size={size}
            style={{ width: '100%' }}
            onClick={save}
            primary={true}
            isDisabled={!isValid}
          >
            {t('save')}
          </Button>
        </Flex>
      </ServicesModalWrapper>
    </ModalFlex>
  )
}

export default withTranslation()(ProvenancesModal)

export interface IProvenancesModalProps {
  open?: boolean
  provenances?: IProvenance[]
  provenancesWithInitial?: IProvenance[]
}

export interface IProvenancesModalDispatch {
  close?: () => void
  addProvenance?: (provenance: string) => void
  deleteProvenance?: (id: number) => () => void
}
